import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CurrencyPipe } from '@angular/common';

import { DialogService } from 'primeng/dynamicdialog';
import { CalculationService, DataService, HelperService } from 'app/services';
import { config } from 'app/constants';
import { currency } from 'app/constants/currency.constants';

import { find, findIndex, sum } from 'lodash';
import { ConfirmDismissDialogComponent } from 'app/modules/shared/components';
import { chargeAccountLimitTooltip } from 'app/constants/csv/constants';

@Component({
  selector: 'app-estimate-total',
  templateUrl: './estimate-total.component.html',
  styleUrls: ['./estimate-total.component.scss'],
  providers: [DialogService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EstimateTotalComponent implements OnInit {
  @Input() isArchived: boolean;
  @Input() orderTitle: string = '';
  @Input() transactions: any[] = [];
  @Input() total: number = 0;
  @Input() cash: number = 0;
  @Input() card: number = 0;
  @Input() cheque: number = 0;
  @Input() creditPayment: number = 0;
  @Input() thirdParty: number = 0;
  @Input() allRefunds: number = 0;
  @Input() taxes: any[] = [];
  @Input() showPaymentLoader: boolean = false;
  @Input() isBalanceNegative: any;
  @Input() companyShopSuppliesEnabled: boolean = false;
  @Input() isPassThroughTaxEnable: boolean = false;
  @Input() isTireStorageEnabled: boolean = false;
  @Input() isRevenueRecognitionSettleEnabled: boolean = false;
  @Input() isChargeAccountEnabled: boolean = false;
  @Input() isPOFieldsReqForDeliveryEnabled: boolean = false;
  @Input() isRoLevelFeeEnabled: boolean = false;
  @Input() isDiscountItemEnabled: boolean = false;
  @Input() defaultShopSupplies: any = {};
  @Input() defaultTaxForCalc: any = {};
  @Input() taxMap: any = {};
  @Input() isTaxDetailsBreakDown: boolean = false;
  @Input() finalizeType: string = '';
  @Input() canFinalizedAdjustmentInvoices: string = '';
  @Input() isSubletTrackingEnabled: boolean = false;
  @Input() isWriteOffEnable: boolean = false;
  @Input() currentLang: string = 'en';
  @Input() companyLocalization: boolean = false;
  @Input() isUpdatingServices: boolean = false;
  
  @Input('roFeesList')
  set roFeesList(roFeesList: any[]) {
    this._roFeesList = this.helperService.deepCopy(roFeesList);
    this.selectedFees = {};

    if (roFeesList) {
      roFeesList.forEach((fee: any) => {
        this.selectedFees[fee.feeId] = fee;
      });
    }
  }
  @Input('orderDiscountList')
  set orderDiscountList(orderDiscountList: any[]) {
    this._orderDiscountList = this.helperService.deepCopy(orderDiscountList);
    this.setOrderDiscountOptionList();
  }
  @Input('defaultTax')
  set defaultTax(tax: any) {
    if (tax) {
      this.selectedTax = tax;
      if (!this.selectedTax.value) this.selectedTax.value = this.selectedTax.salesTaxRate;
    }
  }
  @Input('grand')
  set grand(grand: any) {
    this.setTooltips();
    this.setServiceLevelDiscount();
  }

  @Input('writeOffPaymentData')
  set setWriteOffPaymentData(value: number) {
    this.writeOffPayment = value || 0;
  }

  @Input('overAgePaymentData')
  set overAgePaymentData(value: number) {
    this.overAgePayment = value || 0;
  }

  @Input('estimate')
  set estimate(data: any) {
    if (data) {
      this._estimate = data;
      this.selectedDiscount = this.helperService.deepCopy(this._estimate.total.discount) || {};

      if (data.total && data.total.discount && data.total.discount.type) {
        this.selectedDiscount.value = data.total.discount.value;
      }

      // if(data.total.overAgePayment) {
      //   this.overAgePayment = data.total.overAgePayment;
      // }

      if (this._estimate.customer && this._estimate.customer.customTaxCode && this.dataService.isFleetTaxOverRideEnabled) {
        this.showCustomTaxCode = true;
      }

      // check charge account limites and other variabled
      this.labelDiscount = this.selectedDiscount?.value;
      this.discountType = this.helperService.deepCopy(this.selectedDiscount?.type);

      if (this.selectedDiscount?.type === 'value') {
        this.selectedDiscount.name = 'Fixed';
      } else {
        this.selectedDiscount.name = 'Percent';
      }

      if (data.total.shopSupplyFee) {
        this.selectedShopSupplies = {
          name: data.total.shopSupplyFee.type === 'percentage' ? 'Percent' : 'Fixed',
          type: data.total.shopSupplyFee.type,
          value: data.total.shopSupplyFee.value,
        };
      }

      if (this._estimate.adjustments && this._estimate.adjustments.isApplied) {
      } else {
        this.discountDistributionTooltip = '';
        this.invoiceDistributionTooltip = '';
        this.shopSuppliesDistributionTooltip = '';
      }

      this.isShowApplyCredit = this._estimate.orderStatus === 'invoice';

      this.setTooltips();
      this.setOrderFeeOptionList();
      this.setServiceLevelDiscount();
      this.setOrderDiscountOptionList();
    }
  }
  @Input('roAllFeeTypesOption')
  set setRoAllFeeTypesOption(roAllFeeTypesOption: Array<any>) {
    this.defaultRoAllFeeTypesOption = this.helperService.deepCopy(roAllFeeTypesOption);
    this.setOrderFeeOptionList();
  }
  @Input('orderLevelDiscountOptions')
  set setOrderLevelDiscountOptions(orderLevelDiscountOptions: Array<any>) {
    this.defaultOrderLevelDiscountOptions = this.helperService.deepCopy(orderLevelDiscountOptions);
    this.setOrderDiscountOptionList();
  }
  @Input('chargeAccountDetails')
  set setChargeAccount(chargeAccountDetails) {
    // check charge account limites and other variabled
    if (chargeAccountDetails?.allowedLimit) {
      this.availableLimit = chargeAccountDetails?.allowedLimit - (chargeAccountDetails?.usedLimit || 0);
      this.isShowChargeAccountTooltip = chargeAccountDetails.customerHasChargeAccount && !chargeAccountDetails.unlimited;
    }
  }

  @Input('passThroughTaxRates')
  set setpassThroughTaxRates(passThroughTaxRates) {
    this.passThroughTaxRates = passThroughTaxRates;
  }

  @Output() setDefaultTax = new EventEmitter();
  @Output() setShopSupplies = new EventEmitter();
  @Output() setDiscount = new EventEmitter();
  @Output() setOrderFees = new EventEmitter();
  @Output() setOrderDiscount = new EventEmitter();
  @Output() openPaymentDialog = new EventEmitter();
  @Output() openInvoiceAdjustmentDialog = new EventEmitter();
  @Output() generateActivity = new EventEmitter();
  @Output() appAdjustOrderAmount = new EventEmitter();
  @Output() appResetOrderAmountAdjustment = new EventEmitter();
  @Output() openItemDialog = new EventEmitter();
  @Output() openApplyCreditDialog = new EventEmitter();

  config: any = config;
  selectedDiscount: any = { name: 'Fixed', type: 'value', value: 0 };
  selectedShopSupplies: any = { name: 'Fixed', type: 'value', value: 0 };
  labelDiscount: any = config.estimate.defaultServiceTotal.discount.value;
  discountType: any = config.estimate.defaultServiceTotal.discount.type;
  selectedTax: any;
  _estimate;
  availableLimit = 0;
  isShowChargeAccountTooltip: boolean = false;
  tooltip = chargeAccountLimitTooltip;
  paidToDate: number = 0;
  discountValue: number = 0;
  invoiceAmount: number = 0;
  usd: string = currency.USD;
  isTotalDetailsVisible: boolean = false;
  isTotalPassThroughVisible: boolean = false;
  isTotalRoDetailsVisible: boolean = false;
  isOrderDiscountDistVisible: boolean = false;
  isServiceDiscountDistVisible: boolean = false;
  hidePostTaxFeeTrash: boolean = true;
  isAutoAddRoFlag: boolean = false;
  closeAfterAction: boolean = false;
  isTaxDetailsVisible: boolean = true;
  isFocus: boolean = false;
  discountTypeOptions: any[] = this.helperService.deepCopy(config.estimate.discountType);
  isAdjustAmountFocus: boolean = false;
  roAdjustAmount: number = 0;
  discountDistributionTooltip: string = '';
  invoiceDistributionTooltip: string = '';
  shopSuppliesDistributionTooltip: string = '';
  defaultRoAllFeeTypesOption: Array<any> = [];
  _roAllFeeTypesOption: Array<any> = [];
  _roFeesList: Array<any> = [];
  defaultOrderLevelDiscountOptions: Array<any> = [];
  _orderLevelDiscountOptions: Array<any> = [];
  _orderDiscountList: Array<any> = [];
  serviceDiscountList: Array<any> = [];
  selectedFees: any = {};
  isShowTaxDetails: boolean = false;
  isShowApplyCredit: boolean = false;
  showCustomTaxCode: boolean = false;
  passThroughTaxRates: any = [];
  overAgePayment: number = 0;
  writeOffPayment: number = 0;

  constructor(
    private dialogService: DialogService,
    public calculationService: CalculationService,
    public dataService: DataService,
    public helperService: HelperService,
    private currencyPipe: CurrencyPipe,
  ) {}

  ngOnInit() {
    // const { isRevenueRecognitionSettleEnabled } = this.helperService.getCompany();
    // this.isRevenueRecognitionSettleEnabled = isRevenueRecognitionSettleEnabled;
    this.paidToDate = sum([
      this.calculationService.roundOff(this.card),
      this.calculationService.roundOff(this.cash),
      this.calculationService.roundOff(this.cheque),
      this.calculationService.roundOff(this.creditPayment),
      this.calculationService.roundOff(this.thirdParty),
    ]);
  }

  toggleOrderDetails(name: string, event: Event) {
    this[name] = !this[name];
  }

  setTooltips() {
    const { total, adjustments } = this._estimate || {
      total: {},
      adjustments: {},
    };

    if (adjustments && adjustments.isApplied) {
      // Discount distribution tooltip
      if (this._estimate.isDiscountItemEnabled) {
        const discountItem = find(this._estimate.total.orderDiscountItems || [], { isDefault: true });

        if (discountItem) {
          this.discountDistributionTooltip = `
              <div class="flex-column font-12">
                <p class="mb-1 font-bold font-white">Current order discount: $${this.calculationService.roundOff(discountItem.discountCalcValue)}</p>
                <p class="mb-1 font-bold font-white text-center">=</p>
                <p class="mb-1 font-white">Original order discount: $${this.calculationService.roundOff(adjustments.actualDiscountAmount)}</p>
                <p class="mb-1 font-bold font-white text-center">+</p>
                <p class="mb-1 font-white">
                  Rounding component: $${this.calculationService.roundOff(discountItem.discountCalcValue - adjustments.actualDiscountAmount)}
                </p>
              </div>`;
        }
      } else {
        this.discountDistributionTooltip = `
            <div class="flex-column font-12">
              <p class="mb-1 font-bold font-white">Current order discount: $${this.calculationService.roundOff(total.discountedAmount)}</p>
              <p class="mb-1 font-bold font-white text-center">=</p>
              <p class="mb-1 font-white">Original order discount: $${this.calculationService.roundOff(adjustments.actualDiscountAmount)}</p>
              <p class="mb-1 font-bold font-white text-center">+</p>
              <p class="mb-1 font-white">
                Rounding component: $${this.calculationService.roundOff(total.discountedAmount - adjustments.actualDiscountAmount)}
              </p>
            </div>`;
      }

      // Invoice distribution tooltip
      this.invoiceDistributionTooltip = `
        <div class="flex-column font-12">
          <p class="mb-1 font-bold font-white">
            Current RO value: $${this.calculationService.roundOff(total.grand)}
          </p>
          <p class="mb-1 font-bold font-white text-center">=</p>
          <p class="mb-1 font-white">
            Original RO value: $${this.calculationService.roundOff(adjustments.actualOrderValue)}
          </p>
          <p class="mb-1 font-bold font-white text-center">
            ${adjustments.isRoundDown ? '-' : '+'}
          </p>
          ${
            adjustments.isRoundDown
              ? `<p class="mb-1 font-white">Rounding component: $${this.calculationService.roundOff(adjustments.actualOrderValue - total.grand)}</p>`
              : `<p class="mb-1 font-white">Rounding component: $${this.calculationService.roundOff(total.grand - adjustments.actualOrderValue)}</p>`
          }
        </div>`;

      // Shop supplies distribution tooltip
      if (this._estimate.isRoLevelFeeEnabled) {
        const shopSupplyFee = find(this._estimate.total.preTaxFee || [], { isShopSupply: true });

        if (shopSupplyFee) {
          this.shopSuppliesDistributionTooltip = `
          <div class="flex-column font-12">
            <p class="mb-1 font-bold font-white">
              Current shop supplies: $${this.calculationService.roundOff(shopSupplyFee.feeCalcValue)}
            </p>
            <p class="mb-1 font-bold font-white text-center">=</p>
            <p class="mb-1 font-white">
              Original shop supplies: $${this.calculationService.roundOff(adjustments.actualShopFees)}
            </p>
            <p class="mb-1 font-bold font-white text-center">+</p>
            <p class="mb-1 font-white">
              Rounding component: $${this.calculationService.roundOff(shopSupplyFee.feeCalcValue - adjustments.actualShopFees)}
            </p>
          </div>`;
        }
      } else {
        this.shopSuppliesDistributionTooltip = `
          <div class="flex-column font-12">
            <p class="mb-1 font-bold font-white">
              Current shop supplies: $${this.calculationService.roundOff(total.shopFee)}
            </p>
            <p class="mb-1 font-bold font-white text-center">=</p>
            <p class="mb-1 font-white">
              Original shop supplies: $${this.calculationService.roundOff(adjustments.actualShopFees)}
            </p>
            <p class="mb-1 font-bold font-white text-center">+</p>
            <p class="mb-1 font-white">
              Rounding component: $${this.calculationService.roundOff(total.shopFee - adjustments.actualShopFees)}
            </p>
          </div>`;
      }
    } else {
      // Discount distribution tooltip
      this.discountDistributionTooltip = '';
      // Invoice distribution tooltip
      this.invoiceDistributionTooltip = '';
      // Shop supplies distribution tooltip
      this.shopSuppliesDistributionTooltip = '';
    }
  }

  setOrderFeeOptionList() {
    if (this.defaultRoAllFeeTypesOption.length && this._roFeesList) {
      const feeIds = this._roFeesList.map(({ feeId, _id }) => feeId || _id);
      this._roAllFeeTypesOption = this.defaultRoAllFeeTypesOption.filter(({ _id }) => !feeIds.includes(_id));
    }
  }

  setOrderDiscountOptionList() {
    if (!this._orderDiscountList) {
      this._orderDiscountList = [];
    }

    if (this.defaultOrderLevelDiscountOptions.length && this._orderDiscountList) {
      const discountIds = this._orderDiscountList.map(({ discountId }) => discountId);
      this._orderLevelDiscountOptions = this.defaultOrderLevelDiscountOptions.filter(({ _id }) => !discountIds.includes(_id));
    }
  }

  setServiceLevelDiscount() {
    if (this._estimate && this._estimate.services && this._estimate.services.length) {
      this.serviceDiscountList = [];
      this._estimate.services.forEach(({ includeInInvoice, items }) => {
        if (includeInInvoice) {
          if (items && items.length) {
            items.forEach(({ type, title, netPrice, titleFR }) => {
              if (type === 'discount') {
                this.serviceDiscountList.push({ title, titleFR, amount: netPrice });
              }
            });
          }
        }
      });
      this.serviceDiscountList = [...this.serviceDiscountList];
    }
  }

  onTaxChange(tax: any) {
    this.generateActivity.emit({
      entityId: this._estimate._id,
      entityType: 'order',
      entityAction: 'updated',
      activityData: { type: 'plaintext' },
      title: 'Tax updated',
      text: `RO# ${this._estimate.code}: Tax ${this.selectedTax.name} was updated to ${tax.name}`,
    });
    this.selectedTax = tax;
    this.showCustomTaxCode = false;
    this.setDefaultTax.emit(tax);
  }

  onDiscountChange(type: any, event: any) {
    if (type === 'type') {
      if (event.type === 'percentage') {
        event['name'] = 'Percent';
      } else {
        event['name'] = 'Fixed';
      }

      this.selectedDiscount = event;
    } else {
      this.selectedDiscount.value = event;
    }
  }

  onShopSuppliesChange(name: string, value: any) {
    if (name === 'type') {
      this.selectedShopSupplies.type = value.type;
      this.selectedShopSupplies.name = value.type === 'percentage' ? 'Percent' : 'Fixed';
      this.selectedShopSupplies.value = '';
    } else {
      this.selectedShopSupplies.value = value;
    }
  }

  onOrderFeeChange(name: string, value: any, feeItem: any) {
    if (name === 'type') {
      this.selectedFees[feeItem.feeId].type = value.type;
      this.selectedFees[feeItem.feeId].value = feeItem?.value;
      feeItem.type = value.type;
      if (this.selectedFees[feeItem.feeId].type === 'percentage' && this.selectedFees[feeItem.feeId].value > 100) {
        this.selectedFees[feeItem.feeId].value = 0;
        feeItem.value = 0;
      }
    } else {
      this.selectedFees[feeItem.feeId].value = value;
      feeItem.value = value;
    }
  }

  doneSuppliesFees(close: any) {
    if (close) {
      close();
    }

    const shopSupplies: any = {
      value: this.calculationService.roundOff(this.selectedShopSupplies.value),
      type: this.selectedShopSupplies.type,
    };

    if (this._estimate.total && this._estimate.total.shopSupplyFee && this._estimate.total.shopSupplyFee.cap) {
      shopSupplies.cap = this._estimate.total.shopSupplyFee.cap;
    }
    this.setShopSupplies.emit(shopSupplies);
  }

  doneOrderFee(close: any, feeId: string, isShopSupply: boolean = false) {
    if (close) {
      close();
    }

    let index = findIndex(this._roFeesList, { feeId });
    const updatedPreTaxFee = {
      [feeId]: 'updated',
    };

    let text = '';
    const prevDiscountValue = this._roFeesList[index].feeCalcValue;
    setTimeout(() => {
      text = `$${Number(prevDiscountValue).toFixed(2)} to
      $${Number(this._roFeesList[index].feeCalcValue).toFixed(2)}`;
      this.generateActivity.emit({
        entityId: this._estimate._id,
        entityType: 'order',
        entityAction: 'updated',
        activityData: { type: 'plaintext' },
        title: 'RO fees updated',
        text: `RO# ${this._estimate.code}: RO Fees ${this._roFeesList[index].name} was updated from ${text}`,
        isPrimary: false,
      });
    }, 3000);

    if (index === -1 && isShopSupply) {
      index = findIndex(this._roFeesList, { isShopSupply: true });
    }

    if (index !== -1) {
      this._roFeesList[index].value = this.selectedFees[feeId].value;
      this._roFeesList[index].type = this.selectedFees[feeId].type;
      this.setOrderFees.emit({ preTaxFee: this._roFeesList, updatedPreTaxFee });
    }
  }

  renderDiscount() {
    const discount = this.helperService.deepCopy(this._estimate.total.discount);

    if (discount.type === 'percentage') {
      return discount.value || 0;
    } else {
      if (this._estimate.total.total) {
        return (discount.value / this._estimate.total.total) * 100;
      } else {
        return 0;
      }
    }
  }

  handleOpenDiscount() {
    if (this._estimate.total && this._estimate.total.discount && this._estimate.total.discount.type) {
      this.selectedDiscount = this.helperService.deepCopy(this._estimate.total.discount);
      this.selectedDiscount.name = this._estimate.total.discount.type === 'percentage' ? 'Percent' : 'Fixed';
    }
  }

  handleOpenShopSupplies() {
    if (this._estimate.total && this._estimate.total.shopSupplyFee && this._estimate.total.shopSupplyFee.type) {
      this.selectedShopSupplies = this.helperService.deepCopy(this._estimate.total.shopSupplyFee);

      this.selectedShopSupplies.name = this._estimate.total.shopSupplyFee.type === 'percentage' ? 'Percent' : 'Fixed';
    }
  }

  doneDisc(close: any) {
    this.labelDiscount = this.selectedDiscount.value;
    this.discountType = this.selectedDiscount.type;

    let total = 0;
    let estimate = this.helperService.deepCopy(this._estimate);
    estimate = this.calculationService.estimateServices(estimate, this.helperService.deepCopy(this.defaultTaxForCalc), this.selectedDiscount);

    if (this._estimate.total) {
      const { card = 0, cash = 0, cheque = 0, creditPayment = 0, thirdParty = 0 } = this._estimate;
      const paid = sum([
        this.calculationService.roundOff(card),
        this.calculationService.roundOff(cheque),
        this.calculationService.roundOff(cash),
        this.calculationService.roundOff(creditPayment),
        this.calculationService.roundOff(thirdParty),
      ]);
      // 20 200 - 220
      total = estimate.total.grand - paid;
    }

    if (!this.estimate?.invoiceType && total < 0) {
      this.helperService.showToaster('error', 'Discount cannot be greater than remaining amount');
    } else {
      let amount = this.selectedDiscount.type === 'value' ? this.selectedDiscount.value : this._estimate.total.total * (this.selectedDiscount.value / 100);
      amount = Number(amount).toFixed(2);

      // tslint:disable-next-line: max-line-length
      let text = `RO# ${this._estimate.code}: Discount for amount $${amount} was added to RO`;

      if (this._estimate.total.discountedAmount) {
        text = `RO# ${this._estimate.code}: RO discount was updated from $${this.calculationService.roundOff(this._estimate.total.discountedAmount).toFixed(2)} to $${amount}`;
        text = `RO# ${this._estimate.code} - ${
          this._estimate.title
          // tslint:disable-next-line: max-line-length
        }: RO discount was updated to $${amount} from $${this.calculationService.roundOff(this._estimate.total.discountedAmount).toFixed(2)}`;
      }

      this.generateActivity.emit({
        entityId: this._estimate._id,
        entityType: 'order',
        entityAction: 'updated',
        activityData: { type: 'plaintext' },
        title: this._estimate.total.discountedAmount ? 'RO discount updated' : 'RO discount added',
        text,
      });
      this.setDiscount.emit({
        value: this.calculationService.roundOff(this.selectedDiscount.value),
        type: this.selectedDiscount.type,
      });
      close();
    }
  }

  addPayment() {
    if (this._estimate.invoiceType && this._estimate.total.grand < 0) {
      this.adjustInvoicePayment();
      return;
    }

    this.openPaymentDialog.emit();
  }

  adjustInvoicePayment() {
    this.openInvoiceAdjustmentDialog.emit();
  }

  openApplyCredit() {
    this.openApplyCreditDialog.emit();
  }

  renderEstimatetotal() {
    const { parts, labor, tire, other, itemDiscountedAmount, discountedAmount, taxedAmount, shopFee } = this._estimate.total;
    return parts + labor + tire + other + taxedAmount - itemDiscountedAmount - discountedAmount;
  }

  renderSubtotal() {
    const { parts, labor, other, tire, itemDiscountedAmount, discountedAmount } = this._estimate.total;
    return parts + labor + tire + other - itemDiscountedAmount - discountedAmount;
  }

  renderPaidToDate() {
    const { cash, cheque, card, creditPayment = 0, thirdParty = 0 } = this._estimate.total;

    return sum([
      this.calculationService.roundOff(card),
      this.calculationService.roundOff(cheque),
      this.calculationService.roundOff(cash),
      this.calculationService.roundOff(creditPayment),
      this.calculationService.roundOff(thirdParty),
    ]);
  }

  renderRemainingAmount() {
    this.paidToDate = sum([
      this.calculationService.roundOff(this.card),
      this.calculationService.roundOff(this.cheque),
      this.calculationService.roundOff(this.cash),
      this.calculationService.roundOff(this.creditPayment),
      this.calculationService.roundOff(this.thirdParty),
    ]);
    const tax = this.calculationService.roundOff(this._estimate.total.taxedAmount);
    const serviceDiscount = this.calculationService.roundOff(this._estimate.total.itemDiscountedAmount);
    const { type, value } = this._estimate.total?.discount || {};
    let totalDiscount = 0;
    if (type && value) {
      totalDiscount = type === 'percentage' ? this._estimate.total.total * (value / 100) : value;
    }
    totalDiscount = this.calculationService.roundOff(totalDiscount);

    this.discountValue = this.calculationService.roundOff(tax - serviceDiscount - totalDiscount);

    if (this.finalizeType === 'thirdPartyAccounts' || this.overAgePayment) {
      return this.overAgePayment + (this.total - this.paidToDate) - this.writeOffPayment;
    }

    if (this.finalizeType !== 'thirdPartyAccounts') {
      return this.total - this.paidToDate - this.writeOffPayment;
    }

    return this.total - this.paidToDate;
  }

  onAdjustROValueChange(value: number) {
    this.roAdjustAmount = value;
  }

  adjustOrderAmount(close: any = null) {
    if (close) close();

    this.appAdjustOrderAmount.emit(this.roAdjustAmount);
  }

  resetOrderAmountAdjustment(event: Event) {
    event.preventDefault();
    if(!this.isArchived) {
      this.appResetOrderAmountAdjustment.emit(this.roAdjustAmount);
    }
    
  }

  showItemsRawTotal() {
    if (this._estimate && this._estimate.total) {
      const { total, hazmatFee } = this._estimate.total;
      return Number(total) - Number(hazmatFee);
    }

    return 0;
  }

  showPassThroughRawTotal() {
    if (this._estimate && this._estimate.total) {
      const { total, hazmatFee } = this._estimate.total;
      return 34;
    }

    return 0;
  }

  generateTooltip() {
    let tooltip = ``;

    if (this._estimate) {
      const { taxValues = {}, total = {} } = this._estimate;

      if (this._estimate.customer && this._estimate.customer.customTaxCode && this.dataService.isFleetTaxOverRideEnabled && this.showCustomTaxCode) {
        taxValues.taxRates = this._estimate.customer.customTaxCode.taxRates;
        // total.tax = this._estimate.customer.customTaxCode;
        // total.tax.value = this._estimate.customer.customTaxCode.salesTaxRate;
        total.tax = {
          ...total,
          tax: this._estimate.customer.customTaxCode,
          value: this._estimate.customer.customTaxCode.salesTaxRate,
        };
      }

      let hasTaxRates = taxValues.taxRates && taxValues.taxRates.length;
      if (hasTaxRates) {
        taxValues.taxRates.forEach(({ name, salesTaxRate, taxAmount }, index) => {
          tooltip += `<div class="flex-start-center tax-tooltip-row">
                          <span class="text-truncate pr-1 tax-name">${name}</span>
                          <span class="tax-percentage">${salesTaxRate}%</span>
                          <span class="tax-amount">${this.currencyPipe.transform(Number(taxAmount), this.usd)}</span>
                        </div>`;

          if (taxValues.taxRates.length - 1 !== index) {
            tooltip += `<div class="flex-start-center tax-tooltip-row">
                            <span class="text-center">+</span>
                            <span class="text-center">+</span>
                            <span class="text-center">+</span>
                          </div>`;
          }
        });
      }

      if (total.tax) {
        tooltip += `<div class="flex-start-center tax-tooltip-row ${hasTaxRates ? 'mt-2' : ''}">
                      <span class="text-truncate pr-1 tax-name">${total.tax.name}</span>
                      <span class="tax-percentage">${total.tax.value}%</span>
                      <span class="tax-amount">${this.currencyPipe.transform(Number(total?.taxedAmount), this.usd)}</span>
                    </div>`;
      }
    }

    return `<div class="flex-column">${tooltip}</div>`;
  }

  handleSelectFees(roFeeType: any, close: any) {
    const updatedPreTaxFee = {
      [roFeeType._id]: 'added',
    };
    this._roFeesList.push({ ...roFeeType, feeId: roFeeType._id });
    this.setOrderFees.emit({ preTaxFee: this._roFeesList, updatedPreTaxFee });
    this._roAllFeeTypesOption = this._roAllFeeTypesOption.filter(({ _id }) => roFeeType._id !== _id);
    let text = '';
    setTimeout(() => {
      let index = findIndex(this._roFeesList, { feeId: roFeeType._id });
      text = `$${Number(this._roFeesList[index].feeCalcValue).toFixed(2)}`;
      this.generateActivity.emit({
        entityId: this._estimate._id,
        entityType: 'order',
        entityAction: 'updated',
        activityData: { type: 'plaintext' },
        title: 'RO fees added',
        text: `RO# ${this._estimate.code}: RO Fees ${roFeeType.name} of value ${text} was added`,
        isPrimary: false,
      });
    }, 1000);
    if (close) close();
  }

  deleteROFee(index: any) {
    if (this.isArchived) return;

    const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
      width: '30%',
      styleClass: 'small-heading-dialog',
      showHeader: false,
      data: {
        styleClassHeading: 'font-14',
        title: `Delete order fee`,
        message: `Are you sure you want to delete this order fee?`,
        button: {
          success: { title: 'Delete', backgroundColor: '#d47877' },
          cancel: { title: 'Cancel' },
        },
      },
    });

    const onClose = ref.onClose.subscribe(({ isSuccess, data }) => {
      if (isSuccess) {
        const removedFee = this._roFeesList[index];
        const updatedPreTaxFee = {
          [removedFee._id]: 'deleted',
        };

        let text = '';
        text = `$${Number(removedFee.feeCalcValue).toFixed(2)}`;

        this.generateActivity.emit({
          entityId: this._estimate._id,
          entityType: 'order',
          entityAction: 'updated',
          activityData: { type: 'plaintext' },
          title: 'RO fees removed',
          text: `RO# ${this._estimate.code}: RO Fees ${removedFee.name} of value ${text} was removed`,
          isPrimary: false,
        });
        this._roFeesList.splice(index, 1);
        this.setOrderFees.emit({ preTaxFee: this._roFeesList, updatedPreTaxFee });
      }
      onClose.unsubscribe();
    });
  }

  handleSelectOrderDiscount(discountItem: any, close: any) {
    if (this.isArchived) return;
    if (!this._estimate.invoiceType && this.isBalanceNegative({ itemType: 'order', actionType: 'create', data: discountItem })) {
      const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
        width: '30%',
        styleClass: 'small-heading-dialog',
        showHeader: false,
        data: {
          styleClassHeading: 'font-14',
          title: `The order value can not be negative.`,
          message: `This discount will be added as a 0$ discount. Are you sure you want to continue?`,
          button: {
            success: { title: 'Add the discount with discount value = $0' },
            cancel: { title: 'Do not add the discount' },
          },
        },
      });

      const onClose = ref.onClose.subscribe(({ isSuccess, data }) => {
        if (isSuccess) {
          const updatedDiscountItem = {
            [discountItem._id]: 'added',
          };

          let text = '';
          text = `$${Number(discountItem.discountValue).toFixed(2)}`;

          this.generateActivity.emit({
            entityId: this._estimate._id,
            entityType: 'order',
            entityAction: 'updated',
            activityData: { type: 'plaintext' },
            title: 'Order discount added',
            text: `RO# ${this._estimate.code}: Order discount ${discountItem.title} of value ${text} was added`,
            isPrimary: false,
          });

          this._orderDiscountList.push({
            discountId: discountItem._id,
            taxable: false,
            isDefault: discountItem.isDefault,
            value: 0,
            cap: discountItem.discountCap,
            type: discountItem.discountType,
            discountCalcValue: 0,
            discountCalcPercent: 0,
            active: discountItem.status,
            qboId: discountItem.qboId,
            name: discountItem.title,
            nameFR: discountItem.titleFR,
          });
          this.setOrderDiscount.emit({ orderDiscountItems: this._orderDiscountList, updatedDiscountItem });
        }

        onClose.unsubscribe();
      });
    } else {
      const updatedDiscountItem = {
        [discountItem._id]: 'added',
      };

      let text = '';
      text = `$${Number(discountItem.discountValue).toFixed(2)}`;

      this.generateActivity.emit({
        entityId: this._estimate._id,
        entityType: 'order',
        entityAction: 'updated',
        activityData: { type: 'plaintext' },
        title: 'Order discount added',
        text: `RO# ${this._estimate.code}: Order discount ${discountItem.title} of value ${text} was added`,
        isPrimary: false,
      });

      this._orderDiscountList.push({
        discountId: discountItem._id,
        taxable: false,
        isDefault: discountItem.isDefault,
        value: discountItem.discountValue,
        cap: discountItem.discountCap,
        type: discountItem.discountType,
        discountCalcValue: 0,
        discountCalcPercent: 0,
        active: discountItem.status,
        qboId: discountItem.qboId,
        name: discountItem.title,
        nameFR: discountItem.titleFR,
        description: discountItem.description,
        descriptionFR: discountItem.descriptionFR,
      });
      this.setOrderDiscount.emit({ orderDiscountItems: this._orderDiscountList, updatedDiscountItem });
    }

    if (close) close();
  }

  handleCreatedDiscount(close: any) {
    this.openItemDialog.emit({
      type: 'discount',
      index: -1,
      title: '',
      description: '',
      itemId: '',
      orderId: this._estimate._id,
      serviceId: 'order-discount',
      serviceItem: {},
    });

    if (close) close();
  }

  handleDeleteOrderDiscount(index: any) {
    if (this.isArchived) return;

    const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
      width: '30%',
      styleClass: 'small-heading-dialog',
      showHeader: false,
      data: {
        styleClassHeading: 'font-14',
        title: `Delete order discount`,
        message: `Are you sure you want to delete this order discount?`,
        button: {
          success: { title: 'Delete', backgroundColor: '#d47877' },
          cancel: { title: 'Cancel' },
        },
      },
    });

    const onClose = ref.onClose.subscribe(({ isSuccess, data }) => {
      if (isSuccess) {
        const removedDiscountItem = this._orderDiscountList[index];
        const updatedDiscountItem = {
          [removedDiscountItem.discountId]: 'deleted',
        };

        this.generateActivity.emit({
          entityId: this._estimate._id,
          entityType: 'order',
          entityAction: 'updated',
          activityData: { type: 'plaintext' },
          title: 'Order discount removed',
          text: `RO# ${this._estimate.code}: Order discount ${this._orderDiscountList[index].name} of value $${Number(this._orderDiscountList[index].value).toFixed(2)} was removed`,
          isPrimary: false,
        });

        this._orderDiscountList.splice(index, 1);
        this.setOrderDiscount.emit({ orderDiscountItems: this._orderDiscountList, updatedDiscountItem });
      }
      onClose.unsubscribe();
    });
  }

  handleOpenItemDialog(discountItem: any, type: string, index: number) {
    if (this.isArchived) return;
    this.openItemDialog.emit({
      type,
      index,
      title: discountItem.name,
      titleFR: discountItem.nameFR,
      description: discountItem.description,
      descriptionFR: discountItem.descriptionFR,
      itemId: discountItem.discountId,
      orderId: this._estimate._id,
      serviceId: 'order-discount',
      serviceItem: {
        itemId: discountItem.discountId,
        title: discountItem.name,
        discountOn: 'order',
        discountType: discountItem.type,
        isTaxable: discountItem.taxable,
        discountValue: discountItem.value,
        discountCap: discountItem.cap,
        qboId: discountItem.qboId,
        isDefault: discountItem.isDefault,
      },
    });
  }

  openCustomerSidebar() {
    if (this.finalizeType === 'notFinalized' && this.isShowChargeAccountTooltip && this.isChargeAccountEnabled && this._estimate?.total?.grand > this.availableLimit) {
      const customer = this.helperService.deepCopy(this._estimate.customer);
      customer._id = this._estimate.customer.customerId;
      customer.action = 'select-finance';
      customer.estimateId = this._estimate._id;
      customer.estimate = this.helperService.deepCopy(this._estimate);

      this.dataService.sendData(this.dataService.typeSidebar('customer', customer.customerId, customer.fullName, customer));
      // this.cdr.detectChanges();
    }
  }
}
